import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import FeatureManagementModal from '../components/superadmin/FeatureManagementModal';
import { useTranslation } from 'react-i18next';

// Move CompanyModal outside of the main component
const CompanyModal = ({ isOpen, onClose, onSubmit, title, isEdit = false, formData, onInputChange, loading }) => {
  if (!isOpen) return null;

  const handleModalClick = (e) => {
    // Prevent modal from closing when clicking inside the modal content
    e.stopPropagation();
  };

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop, not the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSubmit(e);
  };

  return (
    <div 
      className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto"
        onClick={handleModalClick}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button 
            type="button"
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleFormSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Company Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={onInputChange}
                className="input-field"
                autoComplete="off"
                required
              />
            </div>
            <div>
              <label className="label">Domain</label>
              <input
                type="text"
                name="domain"
                value={formData.domain}
                onChange={onInputChange}
                className="input-field"
                placeholder="company-domain"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Plan</label>
              <select
                name="plan"
                value={formData.plan}
                onChange={onInputChange}
                className="input-field"
              >
                <option value="trial">Trial</option>
                <option value="basic">Basic</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="label">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={onInputChange}
                className="input-field"
              >
                <option value="active">Active</option>
                <option value="trial">Trial</option>
                <option value="suspended">Suspended</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Admin Email *</label>
              <input
                type="email"
                name="adminEmail"
                value={formData.adminEmail}
                onChange={onInputChange}
                className="input-field"
                autoComplete="off"
                required
              />
            </div>
            <div>
              <label className="label">{isEdit ? 'Admin Password (leave blank to keep current)' : 'Admin Password *'}</label>
              <input
                type="password"
                name="adminPassword"
                value={formData.adminPassword}
                onChange={onInputChange}
                className="input-field"
                autoComplete="new-password"
                required={!isEdit}
                placeholder={isEdit ? "Leave blank to keep current password" : "Enter admin password"}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={onInputChange}
                className="input-field"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="label">Monthly Billing Amount</label>
              <input
                type="number"
                name="billingAmount"
                value={formData.billingAmount}
                onChange={onInputChange}
                className="input-field"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div>
            <label className="label">Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={onInputChange}
              className="input-field"
              rows="3"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Max Users</label>
              <input
                type="number"
                name="maxUsers"
                value={formData.maxUsers}
                onChange={onInputChange}
                className="input-field"
                min="1"
              />
            </div>
            <div>
              <label className="label">Max Shipments</label>
              <input
                type="number"
                name="maxShipments"
                value={formData.maxShipments}
                onChange={onInputChange}
                className="input-field"
                min="1"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEdit ? 'Update Company' : 'Add Company')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Move DeleteModal outside of the main component
const DeleteModal = ({ isOpen, onClose, onConfirm, company, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center mb-4">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-600 mr-3" />
          <h3 className="text-lg font-medium text-gray-900">Delete Company</h3>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Are you sure you want to delete <strong>{company?.name}</strong>? This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="btn btn-outline"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="btn bg-red-600 hover:bg-red-700 text-white"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete Company'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SuperAdminCompanies = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getCompanies, switchCompany, addCompany, updateCompany, deleteCompany, companiesLoading } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    plan: 'trial',
    status: 'active',
    adminEmail: '',
    adminPassword: '',
    phone: '',
    address: '',
    maxUsers: 5,
    maxShipments: 100,
    billingAmount: 0,
  });

  const companies = getCompanies();

  const resetForm = () => {
    setFormData({
      name: '',
      domain: '',
      plan: 'trial',
      status: 'active',
      adminEmail: '',
      adminPassword: '',
      phone: '',
      address: '',
      maxUsers: 5,
      maxShipments: 100,
      billingAmount: 0,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCompany = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      domain: company.domain,
      plan: company.plan,
      status: company.status,
      adminEmail: company.contact?.adminEmail || '',
      adminPassword: '', // Always empty for editing
      phone: company.contact?.phone || '',
      address: company.contact?.address || '',
      maxUsers: company.settings?.maxUsers || 5,
      maxShipments: company.settings?.maxShipments || 100,
      billingAmount: company.billing?.amount || 0,
    });
    setShowEditModal(true);
  };

  const handleDeleteCompany = (company) => {
    setSelectedCompany(company);
    setShowDeleteModal(true);
  };

  const handleManageFeatures = (company) => {
    setSelectedCompany(company);
    setShowFeatureModal(true);
  };

  const handleFeatureModalSuccess = () => {
    setShowFeatureModal(false);
    setSelectedCompany(null);
    // The modal will automatically trigger a company refresh through the auth context
  };

  const submitAddCompany = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Validate required fields
      if (!formData.name || !formData.adminEmail || !formData.adminPassword) {
        toast.error('Please fill in all required fields including admin password');
        setLoading(false);
        return;
      }

      if (formData.adminPassword.length < 6) {
        toast.error('Admin password must be at least 6 characters long');
        setLoading(false);
        return;
      }

      const result = await addCompany(formData);
      if (result) {
        toast.success('Company and admin account created successfully!');
        setShowAddModal(false);
        resetForm();
      } else {
        toast.error('Failed to add company');
      }
    } catch (error) {
      console.error('Error adding company:', error);
      toast.error(error.message || 'Failed to add company');
    } finally {
      setLoading(false);
    }
  };

  const submitUpdateCompany = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Validate required fields
      if (!formData.name || !formData.adminEmail) {
        toast.error('Please fill in all required fields');
        setLoading(false);
        return;
      }

      const updates = {
        name: formData.name,
        domain: formData.domain,
        plan: formData.plan,
        status: formData.status,
        settings: {
          maxUsers: parseInt(formData.maxUsers),
          maxShipments: parseInt(formData.maxShipments),
        },
        billing: {
          amount: parseFloat(formData.billingAmount),
        },
        contact: {
          adminEmail: formData.adminEmail,
          phone: formData.phone,
          address: formData.address,
        }
      };
      
      const result = await updateCompany(selectedCompany.id, updates);
      if (result) {
        toast.success('Company updated successfully!');
        setShowEditModal(false);
        resetForm();
        setSelectedCompany(null);
      } else {
        toast.error('Failed to update company');
      }
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error(error.message || 'Failed to update company');
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteCompany = async () => {
    setLoading(true);
    
    try {
      const result = await deleteCompany(selectedCompany.id);
      if (result) {
        toast.success('Company deleted successfully!');
        setShowDeleteModal(false);
        setSelectedCompany(null);
      } else {
        toast.error('Failed to delete company');
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      toast.error(error.message || 'Failed to delete company');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { class: 'badge-success', icon: CheckCircleIcon, text: t('common.active') },
      trial: { class: 'badge-warning', icon: ClockIcon, text: 'Trial' },
      suspended: { class: 'badge-danger', icon: ExclamationTriangleIcon, text: 'Suspended' },
      cancelled: { class: 'badge-gray', icon: ExclamationTriangleIcon, text: 'Cancelled' },
    };

    const badge = badges[status] || badges.active;
    const Icon = badge.icon;

    return (
      <span className={`badge ${badge.class} inline-flex items-center`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.text}
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    const colors = {
      trial: 'bg-gray-100 text-gray-800',
      basic: 'bg-blue-100 text-blue-800',
      professional: 'bg-green-100 text-green-800',
      enterprise: 'bg-purple-100 text-purple-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[plan] || colors.basic}`}>
        {plan.charAt(0).toUpperCase() + plan.slice(1)}
      </span>
    );
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         company.domain.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    const matchesPlan = planFilter === 'all' || company.plan === planFilter;
    
    return matchesSearch && matchesStatus && matchesPlan;
  });

  const statistics = [
    {
      title: 'Total Companies',
      value: companies.length,
      icon: BuildingOfficeIcon,
      color: 'blue',
    },
    {
      title: 'Active Companies',
      value: companies.filter(c => c.status === 'active').length,
      icon: CheckCircleIcon,
      color: 'green',
    },
    {
      title: 'Trial Companies',
      value: companies.filter(c => c.status === 'trial').length,
      icon: ClockIcon,
      color: 'orange',
    },
    {
      title: 'Monthly Revenue',
      value: `$${companies.filter(c => c.status === 'active').reduce((sum, c) => sum + (c.billing?.amount || 0), 0).toLocaleString('en-US')}`,
      icon: CurrencyDollarIcon,
      color: 'green',
    },
  ];

  const handleViewCompany = async (companyId) => {
    try {
      await switchCompany(companyId);
      // Navigate to company dashboard using React Router
      navigate('/dashboard');
    } catch (error) {
      console.error('Error switching to company:', error);
      // Handle error if needed
    }
  };

  // Show loading spinner while companies are being loaded
  if (companiesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Companies Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage all client companies and their subscriptions
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button 
            onClick={handleAddCompany}
            className="btn btn-primary inline-flex items-center"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Company
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statistics.map((stat, index) => {
          const Icon = stat.icon;
          const colorClasses = {
            blue: 'bg-blue-50 text-blue-600',
            green: 'bg-green-50 text-green-600',
            orange: 'bg-orange-50 text-orange-600',
            red: 'bg-red-50 text-red-600',
          };

          return (
            <div key={index} className="card">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${colorClasses[stat.color]}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search companies..."
                className="input-field pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex space-x-4">
            <select
              className="input-field"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select
              className="input-field"
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
            >
              <option value="all">All Plans</option>
              <option value="trial">Trial</option>
              <option value="basic">Basic</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCompanies.map((company) => (
          <div key={company.id} className="card hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <BuildingOfficeIcon className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{company.name}</h3>
                  <p className="text-sm text-gray-500">{company.domain}.logistiat.com</p>
                </div>
              </div>
              <div className="flex flex-col items-end space-y-2">
                {getStatusBadge(company.status)}
                {getPlanBadge(company.plan)}
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Users:</span>
                  <span className="ml-1 font-medium">{company.stats?.activeUsers || 0}/{company.settings?.maxUsers || 0}</span>
                </div>
                <div>
                  <span className="text-gray-500">Shipments:</span>
                  <span className="ml-1 font-medium">{company.stats?.monthlyShipments || 0}</span>
                </div>
              </div>

              <div className="text-sm">
                <span className="text-gray-500">Monthly Revenue:</span>
                <span className="ml-1 font-medium text-green-600">${company.billing?.amount || 0}</span>
              </div>

              <div className="text-sm">
                <span className="text-gray-500">Created:</span>
                <span className="ml-1 font-medium">{new Date(company.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })}</span>
              </div>

              {company.billing?.nextBilling && (
                <div className="text-sm">
                  <span className="text-gray-500">Next Billing:</span>
                  <span className="ml-1 font-medium">{new Date(company.billing.nextBilling).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  })}</span>
                </div>
              )}
            </div>

            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => handleViewCompany(company.id)}
                className="flex-1 btn btn-primary btn-sm inline-flex items-center justify-center"
              >
                <EyeIcon className="w-4 h-4 mr-1" />
                View
                <ArrowRightIcon className="w-4 h-4 ml-1" />
              </button>
              <button 
                onClick={() => handleManageFeatures(company)}
                className="btn btn-outline btn-sm text-indigo-600 hover:text-indigo-700"
                title="Manage Features"
              >
                <Cog6ToothIcon className="w-4 h-4" />
              </button>
              <button 
                onClick={() => handleEditCompany(company)}
                className="btn btn-outline btn-sm"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button 
                onClick={() => handleDeleteCompany(company)}
                className="btn btn-outline btn-sm text-red-600 hover:text-red-700"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredCompanies.length === 0 && (
        <div className="text-center py-12">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No companies found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding your first client company.
          </p>
          <div className="mt-6">
            <button 
              onClick={handleAddCompany}
              className="btn btn-primary inline-flex items-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Company
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <CompanyModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={submitAddCompany}
        title="Add New Company"
        isEdit={false}
        formData={formData}
        onInputChange={handleInputChange}
        loading={loading}
      />

      <CompanyModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedCompany(null);
        }}
        onSubmit={submitUpdateCompany}
        title="Edit Company"
        isEdit={true}
        formData={formData}
        onInputChange={handleInputChange}
        loading={loading}
      />

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false); 
          setSelectedCompany(null);
        }}
        onConfirm={confirmDeleteCompany}
        company={selectedCompany}
        loading={loading}
      />

      <FeatureManagementModal
        isOpen={showFeatureModal}
        onClose={() => {
          setShowFeatureModal(false);
          setSelectedCompany(null);
        }}
        company={selectedCompany}
        onSuccess={handleFeatureModalSuccess}
      />
    </div>
  );
};

export default SuperAdminCompanies; 